import { useState, useRef, useMemo } from 'react';
import { useLazyGetStockInformationListQuery, useLazyGetStockHistoryListQuery } from '../../../api/endpoints/dealerPartsCatalog';
import { useLanguage } from '../../../contexts/LanguageContext';

export const useStockInformation = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({
        partCode: '',
        pageNumber: 1,
        pageSize: 10
    });
    const [selectedPartCode, setSelectedPartCode] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [first, setFirst] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [triggerStockListSearch, { data: stockData = {}, isLoading: isLoadingStock }] = useLazyGetStockInformationListQuery();
    const [triggerStockHistorySearch, { data: stockHistoryData = {}, isLoading: isLoadingHistory }] = useLazyGetStockHistoryListQuery();

    const rawStockList = stockData?.data?.items || [];
    const totalRecords = stockData?.data?.totalCount || 0;
    const stockHistoryList = stockHistoryData?.data?.items || [];
    const stockHistoryTotalRecords = stockHistoryData?.data?.totalCount || 0;

    const stockList = useMemo(() => {
        if (!sortField || !sortOrder) return rawStockList;

        return [...rawStockList].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string') {
                return sortOrder === 1 ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (typeof aVal === 'number') {
                return sortOrder === 1 ? aVal - bVal : bVal - aVal;
            }

            return 0;
        });
    }, [rawStockList, sortField, sortOrder]);

    const handleSearch = () => {
        const newFilters = { ...filters, pageNumber: 1 };
        setFirst(0);
        setFilters(newFilters);
        if (activeTab === 0) {
            triggerStockListSearch(newFilters);
        } else {
            triggerStockHistorySearch(newFilters);
        }
    };

    // Alt tab'a göre doğru endpoint'e istek at
    const searchByPartCode = (partCode, subTab = activeTab) => {
        const newFilters = { ...filters, partCode, pageNumber: 1 };
        setFirst(0);
        setFilters(newFilters);
        if (subTab === 0) {
            // Stok Bilgisi
            triggerStockListSearch(newFilters);
        } else {
            // Stok Geçmişi
            setSelectedPartCode(partCode);
            triggerStockHistorySearch(newFilters);
        }
    };

    const handleReset = () => {
        setFirst(0);
        setSortField(null);
        setSortOrder(null);
        setFilters({ partCode: '', pageNumber: 1, pageSize: 10 });
        setSelectedPartCode('');
        setActiveTab(0);
    };

    const onPageChange = (event) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setFirst(event.first);
        const newFilters = {
            ...filters,
            pageSize: event.rows,
            pageNumber: newPageNumber
        };
        setFilters(newFilters);
        if (filters.partCode) {
            if (activeTab === 0) {
                triggerStockListSearch(newFilters);
            } else {
                triggerStockHistorySearch(newFilters);
            }
        }
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const handleHistoryClick = (partCode) => {
        setSelectedPartCode(partCode);
        setActiveTab(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString(th('common.locale'));
    };

    const statusBodyTemplate = (rowData) => {
        return rowData.isActive === 1 ? th('common.active') : th('common.inactive');
    };

    const transactionTypeTemplate = (rowData) => {
        const typeMap = {
            1: th('dealerPartsCatalog.stockInformation.transactionTypes.added'),      // Eklendi
            2: th('dealerPartsCatalog.stockInformation.transactionTypes.removed'),    // Çıkarıldı
            10: th('dealerPartsCatalog.stockInformation.transactionTypes.received'),  // Teslim Alındı
            20: th('dealerPartsCatalog.stockInformation.transactionTypes.returned')   // İade Edildi
        };
        return typeMap[rowData.transactionType] || th('common.unknownError');
    };

    return {
        toast,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        selectedPartCode,
        showFilters,
        setShowFilters,
        stockList,
        totalRecords,
        stockHistoryList,
        stockHistoryTotalRecords,
        isLoadingStock,
        isLoadingHistory,
        handleSearch,
        searchByPartCode,
        handleReset,
        handleHistoryClick,
        formatDate,
        statusBodyTemplate,
        transactionTypeTemplate,
        first,
        onPageChange,
        onSort,
        sortField,
        sortOrder,
        th
    };
};
